import React from 'react';
import { Box, Typography, Stack, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import ExternalLinkMapIcon from '../icons/ExternalLinkMapIcon';
import StarRatingMapIcon from '../icons/StarRatingMapIcon';
import DropdownArrowMapIcon from '../icons/DropdownArrowMapIcon';
import BusinessExternalIcon from '../icons/BusinessExternalIcon';
import StarRatingBusinessIcon from '../icons/StarRatingBusinessIcon';

// Mobile-First Styled Components
const ListingsContainer = styled(Stack)(({ theme }) => ({
  gap: '12px',
  height: '100%',
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    gap: '16px',
  },
}));

const YouCard = styled(Box)(({ theme }) => ({
  backgroundColor: '#0B91D6',
  borderRadius: '10px',
  padding: '12px',
  color: '#ffffff',
  width: '100%',
  boxShadow: '0 2px 8px rgba(11, 145, 214, 0.25)',
  [theme.breakpoints.up('sm')]: {
    padding: '16px',
    borderRadius: '8px',
  },
}));

const YouHeader = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '8px',
}));

const YouLabel = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '10px',
  fontWeight: 600,
  color: 'rgba(255, 255, 255, 0.9)',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  padding: '3px 8px',
  borderRadius: '4px',
  [theme.breakpoints.up('sm')]: {
    fontSize: '12px',
    fontWeight: 500,
    backgroundColor: 'transparent',
    padding: 0,
  },
}));

const BusinessName = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '13px',
  fontWeight: 600,
  color: '#ffffff',
  marginBottom: '2px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  maxWidth: '180px',
  [theme.breakpoints.up('sm')]: {
    fontSize: '14px',
    fontWeight: 500,
    marginBottom: '4px',
    maxWidth: '220px',
  },
}));

const BusinessRank = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '11px',
  fontWeight: 600,
  color: 'rgba(255, 255, 255, 0.9)',
  marginLeft: '6px',
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  padding: '2px 6px',
  borderRadius: '4px',
  [theme.breakpoints.up('sm')]: {
    fontSize: '12px',
    fontWeight: 500,
    marginLeft: '8px',
    backgroundColor: 'transparent',
    padding: 0,
  },
}));

const BusinessAddress = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '11px',
  fontWeight: 400,
  color: 'rgba(255, 255, 255, 0.85)',
  marginBottom: '8px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  [theme.breakpoints.up('sm')]: {
    fontSize: '12px',
  },
}));

const ReviewsRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: '6px',
  flexWrap: 'wrap',
  [theme.breakpoints.up('sm')]: {
    gap: '8px',
  },
}));

const ReviewsLabel = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '11px',
  fontWeight: 400,
  color: 'rgba(255, 255, 255, 0.85)',
  [theme.breakpoints.up('sm')]: {
    fontSize: '12px',
    color: '#ffffff',
  },
}));

const ReviewsRating = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '11px',
  fontWeight: 500,
  color: '#ffffff',
  [theme.breakpoints.up('sm')]: {
    fontSize: '12px',
    fontWeight: 400,
  },
}));

const SortSection = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
  marginBottom: '8px',
  padding: '8px 10px',
  backgroundColor: '#F8FAFC',
  borderRadius: '8px',
  border: '1px solid #E2E8F0',
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    marginBottom: '16px',
    padding: 0,
    backgroundColor: 'transparent',
    border: 'none',
    gap: '20px',
  },
  [theme.breakpoints.up('lg')]: {
    gap: '131px',
  },
}));

const SortText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  fontWeight: 500,
  color: '#64748b',
  [theme.breakpoints.up('sm')]: {
    fontSize: '14px',
    color: '#30302E',
  },
}));

const BusinessCard = styled(Box)(({ theme }) => ({
  border: '1px solid #E2E8F0',
  borderRadius: '10px',
  padding: '10px',
  backgroundColor: '#ffffff',
  width: '100%',
  transition: 'all 0.2s ease',
  '&:active': {
    backgroundColor: '#F8FAFC',
    transform: 'scale(0.99)',
  },
  [theme.breakpoints.up('sm')]: {
    border: '0.6px solid #F6F0F0',
    borderRadius: '7px',
    padding: '12px',
    '&:active': {
      transform: 'none',
    },
  },
}));

const BusinessHeader = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
  marginBottom: '4px',
  [theme.breakpoints.up('sm')]: {
    marginBottom: '2px',
  },
}));

const BusinessNameRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: '4px',
  flex: 1,
  minWidth: 0,
  [theme.breakpoints.up('sm')]: {
    gap: '3px',
  },
}));

const BusinessNameText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '13px',
  fontWeight: 600,
  color: '#121927',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  [theme.breakpoints.up('sm')]: {
    fontSize: '14px',
    fontWeight: 500,
  },
}));

const BusinessRankText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '11px',
  fontWeight: 500,
  color: '#64748b',
  backgroundColor: '#F1F5F9',
  padding: '2px 6px',
  borderRadius: '4px',
  flexShrink: 0,
  [theme.breakpoints.up('sm')]: {
    fontSize: '12px',
    color: '#A0A0AA',
    backgroundColor: 'transparent',
    padding: 0,
  },
}));

const BusinessAddressText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '11px',
  fontWeight: 400,
  color: '#64748b',
  marginBottom: '6px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  [theme.breakpoints.up('sm')]: {
    fontSize: '12px',
    color: '#30302E',
    marginBottom: '5px',
  },
}));

const BusinessReviewsRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: '6px',
  flexWrap: 'wrap',
  [theme.breakpoints.up('sm')]: {
    gap: '8px',
  },
}));

const BusinessReviewsLabel = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '11px',
  fontWeight: 400,
  color: '#64748b',
  [theme.breakpoints.up('sm')]: {
    fontSize: '12px',
    color: '#A0A0AA',
  },
}));

const BusinessReviewsRating = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '11px',
  fontWeight: 500,
  color: '#64748b',
  [theme.breakpoints.up('sm')]: {
    fontSize: '12px',
    fontWeight: 400,
    color: '#A0A0AA',
  },
}));

const BusinessListings = ({ businesses, businessName }) => {
  const youBusiness = businesses.find(b => b.isYou);
  const otherBusinesses = businesses.filter(b => !b.isYou);

  return (
    <ListingsContainer>
      {/* YOU Card */}
      {youBusiness && (
        <YouCard>
          <YouHeader>
            <YouLabel>YOU</YouLabel>
            <ExternalLinkMapIcon width={16} height={16} />
          </YouHeader>

          <Stack direction="row" alignItems="center">
            <BusinessName>{youBusiness.name}</BusinessName>
            <BusinessRank>#{youBusiness.rank}</BusinessRank>
          </Stack>

          <BusinessAddress>{youBusiness.address}</BusinessAddress>

          <ReviewsRow>
            <ReviewsLabel>Reviews:</ReviewsLabel>
            <StarRatingMapIcon width={72} height={16} />
            <ReviewsRating>
              {youBusiness.rating ? Number(typeof youBusiness.rating === 'object' ? youBusiness.rating?.value : youBusiness.rating).toFixed(1) : 'N/A'} ({youBusiness.reviews || 0})
            </ReviewsRating>
          </ReviewsRow>
        </YouCard>
      )}

      {/* Sort Section */}
      <SortSection>
        <SortText>Sort by: Business name</SortText>
        <DropdownArrowMapIcon width={12} height={7} />
      </SortSection>

      {/* Other Businesses */}
      <Stack spacing={{ xs: 1, sm: 2 }}>
        {otherBusinesses.map((business, index) => (
          <BusinessCard key={index}>
            <BusinessHeader>
              <BusinessNameRow>
                <BusinessNameText>{business.name}</BusinessNameText>
                <BusinessRankText>#{business.rank}</BusinessRankText>
              </BusinessNameRow>
              <BusinessExternalIcon width={16} height={16} />
            </BusinessHeader>

            <BusinessAddressText>{business.address}</BusinessAddressText>

            <BusinessReviewsRow>
              <BusinessReviewsLabel>Reviews:</BusinessReviewsLabel>
              <StarRatingBusinessIcon width={72} height={16} />
              <BusinessReviewsRating>
                {business.rating ? Number(typeof business.rating === 'object' ? business.rating?.value : business.rating).toFixed(1) : 'N/A'} ({business.reviews || 0})
              </BusinessReviewsRating>
            </BusinessReviewsRow>
          </BusinessCard>
        ))}
      </Stack>
    </ListingsContainer>
  );
};

export default BusinessListings;